import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Navigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import blogPosts from '../data/blogPosts';
import { theme, media } from '../styles/theme';
import { brand } from '../styles/brand';
import { PageContainer, Section } from '../styles/components';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.primary};
  text-decoration: none;
  margin-bottom: ${theme.spacing.md};

  &:hover {
    text-decoration: underline;
  }
`;

const PostHeader = styled.header`
  margin-bottom: ${theme.spacing.lg};
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

const PostDate = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.lightText};
`;

const Tag = styled.span`
  font-size: ${theme.fontSizes.xs};
  background-color: ${theme.colors.background};
  color: ${theme.colors.lightText};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.full};
  padding: 0.15rem 0.6rem;
`;

const PostTitle = styled.h1`
  font-size: ${theme.fontSizes.xxxl};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text};
  line-height: 1.2;
  letter-spacing: ${brand.typography.headingLetterSpacing};

  ${media.tablet} {
    font-size: ${theme.fontSizes.xxl};
  }

  ${media.mobile} {
    font-size: ${theme.fontSizes.xl};
  }
`;

const PostExcerpt = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.lightText};
  line-height: 1.6;
  margin-top: ${theme.spacing.sm};

  ${media.mobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.border};
  margin: ${theme.spacing.lg} 0;
`;

// Prose wrapper — styles the raw HTML from content
const PostContent = styled.article`
  max-width: 720px;
  font-size: ${theme.fontSizes.md};
  line-height: 1.8;
  color: ${theme.colors.text};

  h2 {
    font-size: ${theme.fontSizes.xl};
    font-weight: ${theme.fontWeights.semibold};
    margin-top: ${theme.spacing.lg};
    margin-bottom: ${theme.spacing.sm};
    color: ${theme.colors.text};
  }

  h3 {
    font-size: ${theme.fontSizes.lg};
    font-weight: ${theme.fontWeights.semibold};
    margin-top: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.xs};
  }

  p {
    margin-bottom: ${theme.spacing.sm};
    color: ${theme.colors.lightText};
  }

  ul, ol {
    padding-left: 1.5rem;
    margin-bottom: ${theme.spacing.sm};
    color: ${theme.colors.lightText};
  }

  li {
    margin-bottom: 0.4rem;
  }

  a {
    color: ${theme.colors.primary};
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }

  strong {
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.text};
  }

  /* Tables can outgrow the column on narrow screens, so they scroll inside their own wrapper. */
  .table-wrap {
    overflow-x: auto;
    margin-bottom: ${theme.spacing.sm};
  }

  table {
    border-collapse: collapse;
    width: 100%;
    min-width: 480px;
    font-size: ${theme.fontSizes.sm};
    color: ${theme.colors.lightText};
  }

  th, td {
    border: 1px solid ${theme.colors.border};
    padding: ${theme.spacing.xs};
    text-align: left;
    vertical-align: top;
  }

  th {
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.text};
  }

  figure {
    margin: ${theme.spacing.md} 0;
    text-align: center;
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: ${theme.borderRadius.md};
    margin: 0 auto;
  }

  /* Single-card images shouldn't fill the whole column like the multi-card grids do. */
  figure.card-single img {
    width: auto;
    max-width: min(240px, 100%);
  }

  /* Two images side by side for real-vs-fake comparisons; stacks on narrow screens. */
  .figure-pair {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${theme.spacing.sm};
    margin: ${theme.spacing.md} 0;

    ${media.mobile} {
      grid-template-columns: 1fr;
    }
  }

  .figure-pair figure {
    margin: 0;
  }

  figcaption {
    margin-top: ${theme.spacing.xs};
    font-size: ${theme.fontSizes.sm};
    color: ${theme.colors.lightText};
    font-style: italic;
  }
`;

const FaqSection = styled.section`
  max-width: 720px;
  margin-top: ${theme.spacing.lg};

  h2 {
    font-size: ${theme.fontSizes.xl};
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.text};
    margin-bottom: ${theme.spacing.sm};
  }
`;

const FaqItem = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const FaqQuestion = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const FaqAnswer = styled.p`
  font-size: ${theme.fontSizes.md};
  line-height: 1.8;
  color: ${theme.colors.lightText};
`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/blogg" replace />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    dateModified: post.date,
    url: `https://selg.tryllepapp.no/blogg/${post.slug}`,
    author: {
      '@type': 'Organization',
      name: 'Tryllepapp',
      url: 'https://selg.tryllepapp.no',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Tryllepapp',
      url: 'https://selg.tryllepapp.no',
    },
  };

  // Built from the same post.faq source as the visible section below, so the
  // schema text always matches what's on the page (required by Google).
  const faqSchema =
    post.faq && post.faq.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: post.faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;

  return (
    <Layout>
      <Helmet>
        <title>{`${post.title} | Tryllepapp`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://selg.tryllepapp.no/blogg/${post.slug}`} />
        <meta property="og:title" content={`${post.title} | Tryllepapp`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={`https://selg.tryllepapp.no/blogg/${post.slug}`} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <PageContainer>
        <Section>
          <BackLink to="/blogg">← Tilbake til bloggen</BackLink>

          <PostHeader>
            <PostMeta>
              <PostDate>{formatDate(post.date)}</PostDate>
              {post.tags?.map((tag) => <Tag key={tag}>{tag}</Tag>)}
            </PostMeta>
            <PostTitle>{post.title}</PostTitle>
            <PostExcerpt>{post.excerpt}</PostExcerpt>
          </PostHeader>

          <Divider />

          <PostContent dangerouslySetInnerHTML={{ __html: post.content }} />

          {post.faq && post.faq.length > 0 && (
            <FaqSection>
              <h2>Ofte stilte spørsmål</h2>
              {post.faq.map((item) => (
                <FaqItem key={item.question}>
                  <FaqQuestion>{item.question}</FaqQuestion>
                  <FaqAnswer>{item.answer}</FaqAnswer>
                </FaqItem>
              ))}
            </FaqSection>
          )}
        </Section>
      </PageContainer>
    </Layout>
  );
};

export default BlogPostPage;
